import {
  cookieIdGenerator,
  localStorageIdGenerator,
  sessionStorageIdGenerator,
} from './utils/generators';

/**
 * Identifiers provider for web applications.
 *
 * This provider returns a method 'currentIdentifiers' which returns an object with the
 * identifiers for the current client.
 *
 * Identifiers are UUIDs that live in web storages (localStorage, sessionStorage, cookies)
 * and are used to identify the client. They have different lifecycles for each storage.
 * - localStorage: https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
 * - sessionStorage: https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
 * - cookies: this identifier is set with 1 year retention with a custom domain
 * They can be unwantedly deleted with different logic depending on the browser and user behavior.
 */
export type IdentifiersProvider = {
  /** Returns the current identifiers */
  currentIdentifiers: () => Identifiers;
};

/**
 * Creates an IdentifiersProvider that generates and manages client identifiers across different web storages.
 *
 * @param idKeySuffix - A suffix used to generate unique storage keys for the identifiers
 * @param cookieDomain - The domain to use when setting the cookie identifier
 * @returns An IdentifiersProvider instance that can retrieve current identifiers
 */
export const createIdentifiersProvider = (
  idKeySuffix: string,
  cookieDomain: string,
): IdentifiersProvider => {
  const localStorageId = localStorageIdGenerator(idKeySuffix);
  const sessionStorageId = sessionStorageIdGenerator(idKeySuffix);
  const cookieStorageId = cookieIdGenerator(idKeySuffix, cookieDomain);

  return {
    currentIdentifiers: () => {
      return {
        local_storage_id: localStorageId(),
        session_storage_id: sessionStorageId(),
        cookie_storage_id: cookieStorageId(),
      };
    },
  };
};

/**
 * Identifiers are UUIDs that identify the client in the current tab, device, or domain.
 */
export type Identifiers = {
  /**
   * The unique identifier for the user, stored in localStorage
   */
  local_storage_id: string;
  /**
   * The unique identifier for the user, stored in sessionStorage
   */
  session_storage_id: string;
  /**
   * The unique identifier for the user, stored in cookies
   */
  cookie_storage_id: string;
};
